import {useEffect, useRef} from "react";

import {applyActiveShiftPrimaryProperty} from "@/src/modules/core/lib/apply-active-shift-primary-property";
import {getShiftPrimaryProperty} from "@/src/modules/shifts/lib/get-shift-primary-property";
import {useStoredMeProperties} from "@/src/modules/core/store/me";
import {useUserPropertyStore} from "@/src/modules/core/store/user-property";
import {useActiveShift} from "@/src/modules/officer/hooks/use-active-shift";

/**
 * When an active shift loads or starts, select its primary property in the navbar.
 * Runs once per shift id; manual property changes during the same shift are preserved.
 */
export function useSyncActiveShiftPrimaryProperty() {
  const {shift} = useActiveShift();
  const meProperties = useStoredMeProperties();
  const syncedShiftIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!shift?.shift_id) {
      syncedShiftIdRef.current = null;
      return;
    }
    if (syncedShiftIdRef.current === shift.shift_id) {
      return;
    }
    if (meProperties.length === 0) {
      return;
    }

    const primary = getShiftPrimaryProperty(shift);
    if (!primary) {
      return;
    }

    const current = useUserPropertyStore.getState().userProperty;
    const onPrimary = String(current?.id) === String(primary.property.id);
    if (onPrimary) {
      syncedShiftIdRef.current = shift.shift_id;
      return;
    }

    if (syncedShiftIdRef.current === shift.shift_id) {
      return;
    }

    if (applyActiveShiftPrimaryProperty(shift, meProperties)) {
      syncedShiftIdRef.current = shift.shift_id;
    }
  }, [shift, meProperties]);
}
